import { useEffect, useState, useMemo } from "react"

import { useBookingsContext } from "../hooks/useBookingsContext"
import { useAuthContext } from "../hooks/useAuthContext"

import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar'

import CalendarToolbar from '../components/CalendarToolbar'
import DeleteEventDialog from "./dialogs/DeleteEventDialog"
import LimitDialog from "./dialogs/LimitDialog"
import ConfirmBalconyDialog from "./dialogs/ConfirmBalconyDialog"

import format from "date-fns/format"
import parse from "date-fns/parse"
import startOfWeek from "date-fns/startOfWeek"
import getDay from "date-fns/getDay"
import enUS from "date-fns/locale/sv"

import 'react-big-calendar/lib/css/react-big-calendar.css'
import '../styling/calendar.css'
import { strings } from '../strings'

import { firstAvailableBalconyStartTime, balconyDuration, daysAheadBalcony } from "../config"

const BalconyCalendar = ({ facility }) => {

    const { bookings, bookedBalconySlots, dispatch } = useBookingsContext()
    const [error, setError] = useState(null)
    const [bookingSlots, setBookingSlots] = useState([])
    const { user } = useAuthContext()
    const [open, setOpen] = useState(false)
    const [deleteOpen, setDeleteOpen] = useState(false)
    const [selected, setSelected] = useState(null)
    const [limitOpen, setLimitOpen] = useState(false)
    const [bookedBy, setBookedBy] = useState("")

    const { components } = useMemo(
        () => ({
            components: {
                toolbar: props => (<CalendarToolbar {...props} />)
            }
        }),
        []
    )

    const openConfirmationDialog = () => {
        setOpen(true)
    }

    const confirmBooking = async () => {
        if (!user) {
            return
        }

        setOpen(false)
        setError(null)

        const response = await fetch('/api/bookings/', {
            method: 'POST',
            body: JSON.stringify(selected),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${user.token}`,
            }
        })

        const json = await response.json()

        if (!response.ok) {
            setError(json.msg)
        }

        if (response.ok) {
            setError(null)
            dispatch({ type: 'CREATE_BOOKING', payload: json })
        }
    }

    const cancelBooking = () => {
        setOpen(false)
    }

    const openLimitDialog = () => {
        setLimitOpen(true)
    }

    const closeLimitDialog = () => {
        setLimitOpen(false)
    }

    const openDeleteDialog = () => {
        setDeleteOpen(true)
    }

    const confirmDelete = async () => {

        if (!user) {
            return
        }

        setDeleteOpen(false)
        setError(null)

        const response = await fetch(process.env.REACT_APP_SERVER_URL + '/api/bookings/' + selected._id, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${user.token}`
            }
        })

        const json = await response.json()

        if (!response.ok) {
            setError(json.msg)
        }

        if (response.ok) {
            setError(null)
            dispatch({ type: 'DELETE_BOOKING', payload: json })
            dispatch({
                type: 'SET_BOOKED_BALCONY_SLOTS', payload: bookedBalconySlots.filter(b => {
                    return b._id !== selected._id
                })
            })
        }

        setSelected(null)
    }

    const cancelDelete = () => {
        setDeleteOpen(false)
    }

    const eventPropGetter = (event) => {
        if (bookedBalconySlots) {
            var result = bookedBalconySlots.find(obj => {
                return obj.start === event.start.toISOString()
            })

            var bookingMadeByMe = false
            bookings.forEach(b => {
                if (result) {
                    if (b._id === result._id) {
                        bookingMadeByMe = true
                    }
                }
            })

            const backgroundColor = bookingMadeByMe ? '#6684F0' : result ? '#FA6C0055' : '#00660055';
            return { style: { backgroundColor } }
        }
    }

    useEffect(() => {
        const fetchBookings = async () => {
            setError(null)

            const response = await fetch(process.env.REACT_APP_SERVER_URL + '/api/bookings/balcony', {
                headers: {
                    'Authorization': `Bearer ${user.token}`,
                    'facility': facility
                }
            })
            const json = await response.json()

            if (!response.ok) {
                setError(json.msg)
            }

            if (response.ok) {
                setError(null)
                
                const relevantBookings = json.filter((b) => { 
                    return new Date(b.start) > new Date()
                })
                dispatch({ type: 'SET_BOOKED_BALCONY_SLOTS', payload: relevantBookings })
            }
        }

        if (user) {
            fetchBookings()
        }

    }, [dispatch, user, facility])

    const today = new Date()
    const locales = {
        "sv-SE": enUS,
    }

    const localizer = dateFnsLocalizer({
        format,
        parse,
        startOfWeek: () => { return startOfWeek(new Date(), { weekStartsOn: 1 }) },
        getDay,
        locales,
    })


    useEffect(() => {

        const roundMinutes = (date) => {

            date.setHours(date.getHours() + Math.floor(date.getMinutes() / 60))
            date.setMinutes(0, 0, 0)

            return date
        }

        const addDays = (date, days) => {
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000))

            return date
        }

        const addHours = (date, hours) => {
            date.setTime(date.getTime() + (hours * 60 * 60 * 1000))

            return date
        }

        const populateSchedule = () => {

            const myEventsList = []

            let dayCounter = 0
            for (let i = 0; i < daysAheadBalcony * 2; i = i + 2) {

                let AMstart = new Date()
                AMstart.setHours(firstAvailableBalconyStartTime)
                roundMinutes(AMstart)
                addDays(AMstart, dayCounter)

                let AMend = new Date(AMstart)
                addHours(AMend, balconyDuration)

                let PMstart = new Date(AMend)
                roundMinutes(PMstart)

                let PMend = new Date(PMstart)
                addHours(PMend, balconyDuration)

                myEventsList.push({
                    id: i,
                    start: AMstart,
                    end: AMend
                })

                myEventsList.push({
                    id: i + 1,
                    start: PMstart,
                    end: PMend
                })

                dayCounter++

            }

            setBookingSlots(myEventsList)
        }

        populateSchedule()

    }, [])

    let w = window.innerWidth
    let h = window.innerHeight

    const webEventTimeFormat = ({ start, end }, culture, localizer) =>
        localizer.format(start, 'kk:mm', 'sv-SE') +
        ' - ' +
        localizer.format(end, 'kk:mm', 'sv-SE')

    const mobileEventTimeFormat = ({ start, end }, culture, localizer) =>
        localizer.format(start, 'kk:mm', 'sv-SE')

    const formats = {
        dayFormat: (date, culture, localizer) => localizer.format(date, 'eee d', 'sv-SE'),
        dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
            localizer.format(start, 'd MMM', 'sv-SE')
            + ' - ' +
            localizer.format(end, 'd MMM', 'sv-SE'),
        eventTimeRangeFormat: h < w ? webEventTimeFormat : mobileEventTimeFormat
    }

    const handleClick = (e) => {

        const facility = strings.balcony.balcony
        const start = e.start
        const duration = balconyDuration
        const userName = user.userID

        const booking = { facility, start, duration, userName }

        var result = bookedBalconySlots.find(obj => {
            return obj.start === e.start.toISOString()
        })

        var bookingMadeByMe = false
        bookings.forEach(b => {
            if (result) {
                if (b._id === result._id) {
                    bookingMadeByMe = true
                    setSelected(b)
                }
            }
        })

        if (bookingMadeByMe) {
            openDeleteDialog()
        }
        else if (result && !bookingMadeByMe) {
            setBookedBy(result.userName)
            openLimitDialog()
        } else {
            setSelected(booking)
            openConfirmationDialog()
        }
    }

    return (
        <div style={{ height: "83vh", marginLeft: "1vw", marginRight: "1vw" }}>
            <ConfirmBalconyDialog open={open} event={selected} closeConfirm={confirmBooking} closeCancel={cancelBooking} />
            <DeleteEventDialog open={deleteOpen} closeConfirm={confirmDelete} closeCancel={cancelDelete} />
            <LimitDialog open={limitOpen} closeDialog={closeLimitDialog} limit={strings.dialogs.alreadyBooked + bookedBy} title={strings.dialogs.limitTitle} />

            <Calendar
                defaultView={Views.WEEK}
                events={bookingSlots}
                localizer={localizer}
                step={10}
                timeslots={1}
                formats={formats}
                components={components}
                onSelectEvent={handleClick}
                min={
                    new Date(
                        today.getFullYear(),
                        today.getMonth(),
                        today.getDate(),
                        firstAvailableBalconyStartTime
                    )
                }
                max={
                    new Date(
                        today.getFullYear(),
                        today.getMonth(),
                        today.getDate(),
                        firstAvailableBalconyStartTime + (2 * balconyDuration), 10
                    )}
                eventPropGetter={eventPropGetter}
            />
            {error && <div className="error">{error}</div>}
        </div>
    )
}

export default BalconyCalendar
